<template>
  <!-- Approve Confirm -->
  <div class="modal fade dq-admin-leave-modal" id="approve_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="confirmApprove">
          <!-- Header -->
          <div class="modal-header dq-modal-header">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-success">
                <i class="ti ti-circle-check"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Approve Leave</h5>
                <div class="dq-subtitle">
                  Confirm approval for this leave request.
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <!-- Body -->
          <div class="modal-body dq-body">
            <div class="dq-summary">
              <div class="dq-summary-main">
                Approve leave request for
                <span class="dq-strong">{{ modalState.employee_name || "-" }}</span
                >?
              </div>

              <div class="dq-chips mt-2">
                <span class="dq-chip">
                  <i class="ti ti-briefcase me-1"></i>
                  {{ modalState.leave_type?.name || "-" }}
                </span>
                <span class="dq-chip dq-chip-soft">
                  <i class="ti ti-calendar-time me-1"></i>
                  {{ modalState.days }} day(s)
                </span>
              </div>
            </div>

            <div class="dq-note mt-3">
              <div class="dq-note-icon">
                <i class="ti ti-info-circle"></i>
              </div>
              <div class="dq-note-text">
                This action updates the leave status to <b>Approved</b> and may
                affect leave balances.
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">
              Cancel
            </button>

            <button class="btn btn-success dq-btn dq-btn-success" type="submit" :disabled="approvingLeave">
              <span
                v-if="approvingLeave"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Approve
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Decline Reason -->
  <div class="modal fade dq-admin-leave-modal" id="decline_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="confirmDecline">
          <!-- Header -->
          <div class="modal-header dq-modal-header dq-header-danger">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-danger">
                <i class="ti ti-circle-x"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Decline Leave</h5>
                <div class="dq-subtitle">
                  Optionally add a reason for better clarity.
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <!-- Body -->
          <div class="modal-body dq-body">
            <div class="dq-summary">
              <div class="dq-summary-main">
                Decline leave request for
                <span class="dq-strong">{{ modalState.employee_name || "-" }}</span
                >?
              </div>

              <div class="dq-chips mt-2">
                <span class="dq-chip">
                  <i class="ti ti-briefcase me-1"></i>
                  {{ modalState.leave_type?.name || "-" }}
                </span>
                <span class="dq-chip dq-chip-soft">
                  <i class="ti ti-calendar-time me-1"></i>
                  {{ modalState.days }} day(s)
                </span>
              </div>
            </div>

            <div class="dq-form mt-3">
              <label class="form-label dq-label">Reason (optional)</label>
              <textarea
                v-model.trim="declineReason"
                class="form-control dq-input dq-textarea"
                rows="4"
                placeholder="e.g. Overlapping schedule / insufficient balance / operational needs..."
              />
              <div class="dq-help mt-2">
                <i class="ti ti-message-circle-2 me-1"></i>
                A clear reason improves transparency and reduces back-and-forth.
              </div>
            </div>

            <div class="dq-note dq-note-danger mt-3">
              <div class="dq-note-icon">
                <i class="ti ti-alert-triangle"></i>
              </div>
              <div class="dq-note-text">
                This action updates the leave status to <b>Declined</b>.
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">
              Cancel
            </button>

            <button class="btn btn-danger dq-btn dq-btn-danger" type="submit" :disabled="approvingLeave">
              <span
                v-if="approvingLeave"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Decline
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Revoke Approved Leave -->
  <div class="modal fade dq-admin-leave-modal" id="revoke_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="confirmRevoke">
          <div class="modal-header dq-modal-header dq-header-danger">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-danger">
                <i class="ti ti-ban"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Revoke Leave</h5>
                <div class="dq-subtitle">
                  Cancel an already approved leave and restore balance.
                </div>
              </div>
            </div>
            <button type="button" class="btn-close dq-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div class="modal-body dq-body">
            <div class="dq-summary">
              <div class="dq-summary-main">
                Revoke approved leave for
                <span class="dq-strong">{{ modalState.employee_name || "-" }}</span>?
              </div>
              <div class="dq-chips mt-2">
                <span class="dq-chip">
                  <i class="ti ti-briefcase me-1"></i>
                  {{ modalState.leave_type?.name || "-" }}
                </span>
                <span class="dq-chip dq-chip-soft">
                  <i class="ti ti-calendar-time me-1"></i>
                  {{ modalState.days }} day(s)
                </span>
              </div>
            </div>
            <div class="dq-form mt-3">
              <label class="form-label dq-label">Reason (optional)</label>
              <textarea
                v-model.trim="revokeReason"
                class="form-control dq-input dq-textarea"
                rows="3"
                placeholder="e.g. Employee returned early / request withdrawn..."
              />
            </div>
          </div>
          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-warning dq-btn" type="submit" :disabled="approvingLeave">
              Revoke Leave
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Bulk Decline -->
  <div class="modal fade dq-admin-leave-modal" id="bulk_decline_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="confirmBulkDecline">
          <div class="modal-header dq-modal-header dq-header-danger">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-danger">
                <i class="ti ti-circle-x"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Decline Selected</h5>
                <div class="dq-subtitle">
                  Decline {{ bulkDeclineIds.length }} pending request(s).
                </div>
              </div>
            </div>
            <button type="button" class="btn-close dq-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div class="modal-body dq-body">
            <label class="form-label dq-label">Reason (optional)</label>
            <textarea
              v-model.trim="declineReason"
              class="form-control dq-input dq-textarea"
              rows="4"
              placeholder="Shared reason for all selected declines..."
            />
          </div>
          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-danger dq-btn dq-btn-danger" type="submit" :disabled="approvingLeave">
              Decline Selected
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import * as bootstrap from "bootstrap";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";

const store = useAdminLeaveStore();

const emit = defineEmits(["approve", "decline", "revoke", "bulk-decline"]);
const modalState = reactive({ id: null, employee_name: "", leave_type: null, days: 0 });
const declineReason = ref("");
const revokeReason = ref("");
const bulkDeclineIds = ref([]);

const approvingLeave = computed(() => store.loading);

function openApproveModal(record) {
  modalState.id = record.id;
  modalState.employee_name = record.employee_name;
  modalState.leave_type = record.leave_type;
  modalState.days = record.days;
  bootstrap.Modal.getOrCreateInstance(document.getElementById("approve_modal")).show();
}

function openDeclineModal(record) {
  modalState.id = record.id;
  modalState.employee_name = record.employee_name;
  modalState.leave_type = record.leave_type;
  modalState.days = record.days;
  declineReason.value = "";
  bootstrap.Modal.getOrCreateInstance(document.getElementById("decline_modal")).show();
}

function openRevokeModal(record) {
  modalState.id = record.id;
  modalState.employee_name = record.employee_name;
  modalState.leave_type = record.leave_type;
  modalState.days = record.days;
  revokeReason.value = "";
  bootstrap.Modal.getOrCreateInstance(document.getElementById("revoke_modal")).show();
}

function openBulkDeclineModal(ids) {
  bulkDeclineIds.value = [...ids];
  declineReason.value = "";
  bootstrap.Modal.getOrCreateInstance(document.getElementById("bulk_decline_modal")).show();
}

function confirmApprove() {
  emit("approve", modalState.id);
  bootstrap.Modal.getInstance(document.getElementById("approve_modal"))?.hide();
}

function confirmDecline() {
  emit("decline", { id: modalState.id, reason: declineReason.value });
  bootstrap.Modal.getInstance(document.getElementById("decline_modal"))?.hide();
}

function confirmRevoke() {
  emit("revoke", { id: modalState.id, reason: revokeReason.value });
  bootstrap.Modal.getInstance(document.getElementById("revoke_modal"))?.hide();
}

function confirmBulkDecline() {
  emit("bulk-decline", { ids: bulkDeclineIds.value, reason: declineReason.value });
  bootstrap.Modal.getInstance(document.getElementById("bulk_decline_modal"))?.hide();
}

defineExpose({ openApproveModal, openDeclineModal, openRevokeModal, openBulkDeclineModal });
</script>

<style scoped>
/* ===============================
   CONTAINER / WIDTH
================================ */
.dq-admin-leave-dialog {
  max-width: 720px;
}
@media (max-width: 768px) {
  .dq-admin-leave-dialog {
    max-width: 95%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 80px rgba(15, 23, 42, 0.12);
}

/* ===============================
   HEADER
================================ */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.10), #fff);
}

.dq-header-danger {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.10), #fff);
}

.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-title {
  font-weight: 900;
  letter-spacing: -0.3px;
}

.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

.dq-close {
  opacity: 0.8;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.dq-hero-success {
  background: rgba(34, 197, 94, 0.16);
  color: rgb(22, 163, 74);
}

.dq-hero-danger {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* ===============================
   BODY
================================ */
.dq-body {
  padding: 18px 22px;
}

.dq-summary {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  padding: 14px 14px;
  background: rgba(15, 23, 42, 0.02);
}

.dq-summary-main {
  font-size: 0.95rem;
  color: rgba(15, 23, 42, 0.88);
}

.dq-strong {
  font-weight: 900;
}

/* Chips */
.dq-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  color: rgba(15, 23, 42, 0.8);
}

.dq-chip-soft {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.18);
}

/* Notes */
.dq-note {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.18);
}

.dq-note-danger {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.18);
}

.dq-note-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.04);
}

.dq-note-text {
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.78);
}

/* ===============================
   FORM
================================ */
.dq-label {
  font-size: 0.82rem;
  font-weight: 700;
}

.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}

.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
}

.dq-textarea {
  resize: vertical;
  min-height: 110px;
}

.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* ===============================
   FOOTER / BUTTONS
================================ */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}

.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 800;
}

.dq-btn-muted {
  font-weight: 700;
}

.dq-btn-success,
.dq-btn-danger {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}
</style>
